import React, { useState, useMemo, useEffect } from 'react';
import { useApi, apiPut, apiPost, apiDelete } from '../hooks/useApi';
import { apiGet, apiRequest } from '../lib/apiClient';
import { TableSkeleton } from '../components/LoadingSkeleton';
import AccountTypeFilter from '../components/AccountTypeFilter';
import { Check, X, Search, Wand2, LayoutGrid, List, CircleDot, Sparkles, AlertTriangle, ChevronDown, ChevronRight, Pencil, Eye, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const VIEW_MODES = { GROUPED: 'grouped', LIST: 'list', UNMAPPED: 'unmapped' };
const getMagicIdentityKey = (m) => `${m.account_id || 0}:${m.magic || 0}:${String(m.ea_name || "").trim().toLowerCase()}`;
const toMemberPayload = (m) => ({ account_id: m.account_id, magic: m.magic || 0, ea_name: m.ea_name || '' });
const money = (value) => `${(value || 0) >= 0 ? '+' : ''}$${Math.abs(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const folderKey = (id) => `folder:${id}`;
const accountMagicKey = (m) => `${m.account_id || 0}:${m.magic || 0}`;
const autoGroupKey = (name) => `auto:${name}`;

function buildEaGroups(rows, sortBy, sortDir) {
  const map = {};
  rows.forEach(m => {
    const name = m.ea_name || 'Needs Official Mapping';
    if (!map[name]) map[name] = { ea_name: name, magics: [], total_trades: 0, total_profit: 0 };
    map[name].magics.push(m);
    map[name].total_trades += m.total_trades || 0;
    map[name].total_profit += m.net_profit || 0;
  });
  const groups = Object.values(map);
  let result;
  switch (sortBy) {
    case 'trades': result = groups.sort((a, b) => b.total_trades - a.total_trades); break;
    case 'name': result = groups.sort((a, b) => a.ea_name.localeCompare(b.ea_name)); break;
    default: result = groups.sort((a, b) => b.total_profit - a.total_profit);
  }
  return sortDir === 'asc' ? result.reverse() : result;
}

export default function MagicNumbers() {
  const [accountType, setAccountType] = useState('all');
  const accountTypeQuery = accountType === 'all' ? '' : `?type_filter=${accountType}`;
  const { data: magics, loading: loadingMagics, refetch: refetchMagics } = useApi(`/magic-numbers${accountTypeQuery}`);
  const { data: eaStats, loading: loadingStats, refetch: refetchStats } = useApi(`/ea-stats${accountTypeQuery}`);
  const { data: eaGroups, refetch: refetchGroups } = useApi('/ea-groups');
  const [viewMode, setViewMode] = useState(VIEW_MODES.GROUPED);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('profit');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedMagics, setExpandedMagics] = useState({});
  const [editingMagic, setEditingMagic] = useState(null);
  const [editName, setEditName] = useState('');
  const [autoMapLoading, setAutoMapLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [allExpanded, setAllExpanded] = useState(false);
  // Edit popup state
  const [editPopup, setEditPopup] = useState(null); // { magic, ea_name, mql5_url, label }
    const [editGroupName, setEditGroupName] = useState(null); // folder editor state
  const [selectedKeys, setSelectedKeys] = useState({});
  const [moveTargetId, setMoveTargetId] = useState('');

  const handleAccountTypeChange = (nextType) => {
    setAccountType(nextType);
    setSelectedKeys({});
    setExpandedGroups({});
    setExpandedMagics({});
    setAllExpanded(false);
  };

  const loading = loadingMagics || loadingStats;

  // Enrich magic rows with exact account+magic+name stats. Same magic can be different EAs.
  const enrichedMagics = useMemo(() => {
    return (magics || []).map(m => {
      const stat = (eaStats || []).find(s =>
        s.magic === m.magic &&
        s.account_id === m.account_id &&
        String(s.ea_name || '').trim().toLowerCase() === String(m.ea_name || '').trim().toLowerCase()
      ) || (eaStats || []).find(s => s.magic === m.magic && s.account_id === m.account_id);
      return stat ? { ...m, ...stat, identity_key: getMagicIdentityKey({ ...m, ...stat }) } : { ...m, identity_key: getMagicIdentityKey(m) };
    });
  }, [magics, eaStats]);
  // Filter by search
  const filtered = useMemo(() => {
    if (!searchQuery) return enrichedMagics;
    const q = searchQuery.toLowerCase();
    return enrichedMagics.filter(m =>
      String(m.magic).includes(q) ||
      (m.ea_name || '').toLowerCase().includes(q)
    );
  }, [enrichedMagics, searchQuery]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    let result;
    switch (sortBy) {
      case 'profit': result = arr.sort((a, b) => (b.net_profit || 0) - (a.net_profit || 0)); break;
      case 'trades': result = arr.sort((a, b) => (b.total_trades || 0) - (a.total_trades || 0)); break;
      case 'winrate': result = arr.sort((a, b) => (b.win_rate || 0) - (a.win_rate || 0)); break;
      case 'magic': result = arr.sort((a, b) => a.magic - b.magic); break;
      case 'name': result = arr.sort((a, b) => (a.ea_name || '').localeCompare(b.ea_name || '')); break;
      default: result = arr;
    }
    return sortDir === 'asc' ? result.reverse() : result;
  }, [filtered, sortBy, sortDir]);

  // Group by EA name. Auto-detection remains unchanged; folders only hide assigned identities from Unfiled.
  const grouped = useMemo(() => buildEaGroups(sorted, sortBy, sortDir), [sorted, sortBy, sortDir]);

  const magicByKey = useMemo(() => {
    const map = new Map();
    sorted.forEach(m => map.set(getMagicIdentityKey(m), m));
    return map;
  }, [sorted]);

  const magicByAccountMagic = useMemo(() => {
    const map = new Map();
    sorted.forEach(m => {
      if (!map.has(accountMagicKey(m))) map.set(accountMagicKey(m), m);
    });
    return map;
  }, [sorted]);

  const folderedKeys = useMemo(() => {
    const keys = new Set();
    (eaGroups || []).forEach(group => (group.members || []).forEach(member => {
      const exactKey = getMagicIdentityKey(member);
      const row = magicByKey.get(exactKey) || magicByAccountMagic.get(accountMagicKey(member));
      keys.add(row ? getMagicIdentityKey(row) : exactKey);
    }));
    return keys;
  }, [eaGroups, magicByKey, magicByAccountMagic]);

  const manualFolders = useMemo(() => (eaGroups || []).map(group => {
    const magics = (group.members || []).map(member => magicByKey.get(getMagicIdentityKey(member)) || magicByAccountMagic.get(accountMagicKey(member))).filter(Boolean);
    const total_trades = magics.reduce((sum, m) => sum + (m.total_trades || 0), 0);
    const total_profit = magics.reduce((sum, m) => sum + (m.net_profit || 0), 0);
    return { ...group, magics, total_trades, total_profit };
  }), [eaGroups, magicByKey, magicByAccountMagic]);

  const unfiledGrouped = useMemo(() => buildEaGroups(sorted.filter(m => !folderedKeys.has(getMagicIdentityKey(m))), sortBy, sortDir), [sorted, folderedKeys, sortBy, sortDir]);
  const selectedRows = useMemo(() => sorted.filter(m => selectedKeys[getMagicIdentityKey(m)]), [sorted, selectedKeys]);

  // Unmapped magics (no ea_name or auto-generated generic name like "EA_12345")
  const unmapped = useMemo(() => {
    return sorted.filter(m => !m.ea_name || m.ea_name === '' || (m.ea_name.startsWith('EA_') && /^EA_\d+$/.test(m.ea_name)));
  }, [sorted]);

  const handleRename = async (row) => {
    const target = typeof row === 'object' ? row : enrichedMagics.find(m => m.magic === row);
    if (!target || !editName.trim()) return;
    try {
      await apiPut(`/magic-numbers/${target.magic}/rename`, { ea_name: editName.trim(), account_id: target.account_id, old_ea_name: target.ea_name || '' });
      toast.success(`Renamed ${target.magic} to "${editName.trim()}"`);
      setEditingMagic(null);
      refetchMagics();
      refetchStats();
    } catch (e) {
      toast.error('Failed to rename');
    }
  };
  // Open edit popup for a magic number
  const openEditPopup = (m) => {
    setEditPopup({
      magic: m.magic,
      account_id: m.account_id,
      ea_name: m.ea_name || '',
      new_ea_name: '',
      mql5_url: '',
      label: '',
    });
  };

  // Save from edit popup
  const handleSavePopup = async () => {
    if (!editPopup) return;
    const newName = editPopup.new_ea_name.trim() || editPopup.ea_name;
    if (!newName) return;
    try {
      await apiPut(`/magic-numbers/${editPopup.magic}/rename`, { ea_name: newName, account_id: editPopup.account_id, old_ea_name: editPopup.ea_name || '' });
      toast.success(`Magic ${editPopup.magic} updated`);
      setEditPopup(null);
      refetchMagics();
      refetchStats();
    } catch (e) {
      toast.error('Failed to save changes');
    }
  };

  // Create or rename a manual folder. This never renames EAs or trade history.
  const handleRenameGroup = async () => {
    if (!editGroupName || !editGroupName.newName.trim()) return;
    try {
      if (editGroupName.id) {
        await apiPut(`/ea-groups/${editGroupName.id}`, {
          group_name: editGroupName.newName.trim(),
          color: editGroupName.color || '#a855f7',
          description: editGroupName.description || '',
        });
        toast.success('Folder renamed');
      } else {
        await apiPost('/ea-groups', { group_name: editGroupName.newName.trim(), color: editGroupName.color || '#a855f7', description: editGroupName.description || '' });
        toast.success(`Folder "${editGroupName.newName.trim()}" created`);
      }
      setEditGroupName(null);
      refetchGroups();
    } catch (e) {
      toast.error(editGroupName.id ? 'Failed to rename folder' : 'Failed to create folder');
    }
  };

  const handleAutoMap = async () => {
    setAutoMapLoading(true);
    try {
      // First: try the new auto-map from trades endpoint (uses ea_name from trade data)
      const directRes = await apiPost('/auto-map-magics-from-trades', {});
      if (directRes.mapped_count > 0) {
        toast.success(`Auto-mapped ${directRes.mapped_count} EAs from trade data!`);
        refetchMagics();
        refetchStats();
        setAutoMapLoading(false);
        return;
      } else {
        toast(`No EAs to auto-map (${directRes.message || 'all already mapped'})`);
      }
      
      // Fallback: try group suggestions
      const res = await apiPost('/ea-groups/auto-generate', {});
      setSuggestions(res.suggestions || []);
      if (res.suggestions?.length > 0) {
        toast.success(`Found ${res.suggestions.length} group suggestions`);
      }
    } catch (e) {
      console.error('Auto-map error:', e);
      toast.error(`Auto-map failed: ${e.message || 'Unknown error'}`);
    }
    setAutoMapLoading(false);
  };

  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await apiPost('/ea-groups/accept-suggestion', {
        group_name: suggestion.group_name,
        color: suggestion.color,
        members: suggestion.members,
      });
      toast.success(`Group "${suggestion.group_name}" created`);
      setSuggestions(prev => prev.filter(s => s.group_name !== suggestion.group_name));
      refetchGroups();
      refetchMagics();
      refetchStats();
    } catch (e) {
      toast.error('Failed to create group');
    }
  };

  const toggleGroup = (name) => {
    setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleMagic = (row) => {
    const key = typeof row === 'object' ? getMagicIdentityKey(row) : row;
    setExpandedMagics(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleSelected = (row) => {
    const key = getMagicIdentityKey(row);
    setSelectedKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const setRowsSelected = (rows, checked) => {
    setSelectedKeys(prev => {
      const next = { ...prev };
      rows.forEach(row => { next[getMagicIdentityKey(row)] = checked; });
      return next;
    });
  };

  const handleMoveSelected = async () => {
    if (!moveTargetId || selectedRows.length === 0) return;
    try {
      await apiPost(`/ea-groups/${moveTargetId}/members`, { members: selectedRows.map(toMemberPayload) });
      toast.success(`Moved ${selectedRows.length} EA${selectedRows.length === 1 ? '' : 's'} to folder`);
      setSelectedKeys({});
      setMoveTargetId('');
      refetchGroups();
    } catch (e) {
      toast.error('Failed to move selected EAs');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.group_name}"? EAs will return to Unfiled and trade data will not be deleted.`)) return;
    try {
      await apiDelete(`/ea-groups/${folder.id}`);
      toast.success('Folder deleted');
      refetchGroups();
    } catch (e) {
      toast.error('Failed to delete folder');
    }
  };

  const handleRemoveMember = async (folder, row) => {
    if (!window.confirm(`Remove "${row.ea_name || `EA_${row.magic}`}" from "${folder.group_name}"?`)) return;
    try {
      await apiRequest(`/api/ea-groups/${folder.id}/members`, { method: 'DELETE', body: { members: [toMemberPayload(row)] } });
      toast.success('EA returned to Unfiled');
      refetchGroups();
    } catch (e) {
      toast.error('Failed to remove EA from folder');
    }
  };

  const expandAll = () => {
    if (allExpanded) {
      setExpandedGroups({});
      setExpandedMagics({});
    } else {
      const gMap = {};
      manualFolders.forEach(folder => { gMap[folderKey(folder.id)] = true; });
      unfiledGrouped.forEach(g => { gMap[autoGroupKey(g.ea_name)] = true; });
      setExpandedGroups(gMap);
      const mMap = {};
      sorted.forEach(m => { mMap[getMagicIdentityKey(m)] = true; });
      setExpandedMagics(mMap);
    }
    setAllExpanded(!allExpanded);
  };

  if (loading) return <TableSkeleton rows={8} />;

  const detectedCount = enrichedMagics.filter(m => m.ea_name && !m.ea_name.startsWith('EA_')).length;

  return (
    <div style={{ padding: '24px', color: '#e6edf3', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#e6edf3', margin: '0 0 6px' }}>Magic Number Manager</h1>
        <p style={{ color: '#8b949e', fontSize: '13px', margin: 0 }}>
          Map magic numbers to Expert Advisors. Group related magic numbers under the same EA for accurate performance tracking.
        </p>
      </div>

      {/* View and account type filters */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ViewTab icon={<LayoutGrid size={14} />} label={manualFolders.length + unfiledGrouped.length} active={viewMode === VIEW_MODES.GROUPED} onClick={() => setViewMode(VIEW_MODES.GROUPED)} color="#00ff88" />
          <ViewTab icon={<List size={14} />} label={sorted.length} active={viewMode === VIEW_MODES.LIST} onClick={() => setViewMode(VIEW_MODES.LIST)} color="#378ADD" />
          <ViewTab icon={<CircleDot size={14} />} label={unmapped.length} active={viewMode === VIEW_MODES.UNMAPPED} onClick={() => setViewMode(VIEW_MODES.UNMAPPED)} color="#f59e0b" />
        </div>
        <AccountTypeFilter value={accountType} onChange={handleAccountTypeChange} />
      </div>
      {/* Detection Banner */}
      {detectedCount > 0 && viewMode !== VIEW_MODES.UNMAPPED && (
        <div style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Sparkles size={18} style={{ color: '#00ff88' }} />
            <div>
              <p style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600, margin: 0 }}>{detectedCount} EAs Detected from Your Trades</p>
              <p style={{ color: '#8b949e', fontSize: '11px', margin: '2px 0 0' }}>We found EA names in your trade comments. Set them up with one click!</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ padding: '6px 14px', background: 'transparent', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', fontSize: '11px', cursor: 'pointer' }}>Review Individually</button>
            <button onClick={handleAutoMap} disabled={autoMapLoading} style={{ padding: '6px 14px', background: '#00ff88', border: 'none', borderRadius: '6px', color: '#0d1117', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Wand2 size={12} /> {autoMapLoading ? 'Mapping...' : 'Auto-Map All'}
            </button>
          </div>
        </div>
      )}

      {/* Unmapped Banner for unmapped view */}
      {viewMode === VIEW_MODES.UNMAPPED && unmapped.length > 0 && (
        <div style={{ background: '#161b22', border: '1px solid #f59e0b30', borderRadius: '10px', padding: '12px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={14} style={{ color: '#f59e0b' }} />
          <span style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 600 }}>{unmapped.length} unmapped magic numbers</span>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <button onClick={expandAll} style={{ padding: '5px 12px', background: '#21262d', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', fontSize: '11px', cursor: 'pointer' }}>
          {allExpanded ? 'Collapse' : 'Expand'}
        </button>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#484f58' }} />
          <input
            type="text"
            placeholder="Search by name, magic number, or symbol..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 32px', background: '#0d1117', border: '1px solid #21262d', borderRadius: '6px', color: '#e6edf3', fontSize: '12px', outline: 'none' }}
          />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: '7px 12px', background: '#0d1117', border: '1px solid #21262d', borderRadius: '6px', color: '#e6edf3', fontSize: '11px' }}>
          <option value="profit">Sort by Profit</option>
          <option value="trades">Sort by Trades</option>
          <option value="winrate">Sort by Win Rate</option>
          <option value="magic">Sort by Magic #</option>
          <option value="name">Sort by Name</option>
        </select>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => setSortDir(prev => prev === 'desc' ? 'asc' : 'desc')} style={{ padding: '6px 12px', background: sortDir === 'desc' ? '#a855f7' : '#21262d', border: sortDir === 'desc' ? 'none' : '1px solid #30363d', borderRadius: '6px', color: sortDir === 'desc' ? '#fff' : '#8b949e', fontSize: '12px', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }} title="Toggle sort direction">
            <span>{sortDir === 'desc' ? 'Desc' : 'Asc'}</span>
          </button>
          <button onClick={() => setEditGroupName({ oldName: '', newName: '', color: '#a855f7', description: '' })} style={{ padding: '6px 12px', background: '#21262d', border: '1px solid #30363d', borderRadius: '6px', color: '#e6edf3', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }} title="Create new folder">New Folder</button>
        </div>
      </div>
      {selectedRows.length > 0 && (
        <BulkMoveBar
          folders={manualFolders}
          selectedCount={selectedRows.length}
          moveTargetId={moveTargetId}
          setMoveTargetId={setMoveTargetId}
          onMove={handleMoveSelected}
          onClear={() => setSelectedKeys({})}
        />
      )}
      {/* Content based on view mode */}
      {viewMode === VIEW_MODES.GROUPED && (
        <FolderedGroupedView
          manualFolders={manualFolders}
          unfiledGrouped={unfiledGrouped}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          editingMagic={editingMagic}
          editName={editName}
          setEditingMagic={setEditingMagic}
          setEditName={setEditName}
          handleRename={handleRename}
          openEditPopup={openEditPopup}
          setEditGroupName={setEditGroupName}
          handleDeleteFolder={handleDeleteFolder}
          handleRemoveMember={handleRemoveMember}
          selectedKeys={selectedKeys}
          toggleSelected={toggleSelected}
          setRowsSelected={setRowsSelected}
        />
      )}

      {viewMode === VIEW_MODES.LIST && (
        <ListView
          sorted={sorted}
          expandedMagics={expandedMagics}
          toggleMagic={toggleMagic}
          editingMagic={editingMagic}
          editName={editName}
          setEditingMagic={setEditingMagic}
          setEditName={setEditName}
          handleRename={handleRename}
          openEditPopup={openEditPopup}
        />
      )}

      {viewMode === VIEW_MODES.UNMAPPED && (
        <UnmappedView
          unmapped={unmapped}
          editingMagic={editingMagic}
          editName={editName}
          setEditingMagic={setEditingMagic}
          setEditName={setEditName}
          handleRename={handleRename}
          suggestions={suggestions}
          handleAcceptSuggestion={handleAcceptSuggestion}
          openEditPopup={openEditPopup}
          handleIdentify={async (m) => {
            try {
              const res = await apiPost('/auto-map-magics-from-trades', {});
              if (res.mapped_count > 0) {
                toast.success(`Identified! Mapped ${res.mapped_count} EAs`);
                refetchMagics();
                refetchStats();
              } else {
                // Fallback: open the edit popup so user can name it manually
                toast('Could not auto-identify. Please name it manually.');
                openEditPopup(m);
              }
            } catch (e) {
              toast.error('Identify failed');
              openEditPopup(m);
            }
          }}
        />
      )}

      {/* Edit Magic Number Popup */}
      {editPopup && (
        <EditMagicPopup
          editPopup={editPopup}
          setEditPopup={setEditPopup}
          handleSavePopup={handleSavePopup}
          allEANames={[...new Set(enrichedMagics.map(m => m.ea_name).filter(Boolean))]}
        />
      )}

      {/* Rename Group Popup */}
      {editGroupName && (
        <RenameGroupPopup
          editGroupName={editGroupName}
          setEditGroupName={setEditGroupName}
          handleRenameGroup={handleRenameGroup}
        />
      )}
    </div>
  );
}


// === EDIT MAGIC NUMBER POPUP ===
function EditMagicPopup({ editPopup, setEditPopup, handleSavePopup, allEANames }) {
  const [searchEA, setSearchEA] = useState('');
  const filteredEAs = allEANames.filter(n => n.toLowerCase().includes(searchEA.toLowerCase()));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEditPopup(null)}>
      <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '16px', width: '480px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <Pencil size={18} style={{ color: '#a855f7' }} />
          <h2 style={{ color: '#e6edf3', fontSize: '18px', fontWeight: 700, margin: 0 }}>Edit Magic Number</h2>
          <span style={{ color: '#a855f7', fontSize: '18px', fontWeight: 700 }}>{editPopup.magic}</span>
        </div>

        {/* Current EA */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: '#8b949e', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Current EA</label>
          <div style={{ background: '#a855f720', border: '1px solid #a855f7', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Check size={14} style={{ color: '#a855f7' }} />
            <span style={{ color: '#e6edf3', fontSize: '14px', fontWeight: 600 }}>{editPopup.ea_name || 'Unnamed'}</span>
          </div>
        </div>

        {/* Change EA */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: '#8b949e', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Change EA (optional)</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#484f58' }} />
            <input
              type="text"
              placeholder="Search for a different EA..."
              value={editPopup.new_ea_name || searchEA}
              onChange={e => { setSearchEA(e.target.value); setEditPopup(prev => ({ ...prev, new_ea_name: e.target.value })); }}
              style={{ width: '100%', padding: '12px 16px 12px 36px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '13px', outline: 'none' }}
            />
          </div>
          {/* EA suggestions dropdown */}
          {searchEA && filteredEAs.length > 0 && (
            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', marginTop: '4px', maxHeight: '120px', overflowY: 'auto' }}>
              {filteredEAs.slice(0, 5).map(name => (
                <div key={name} onClick={() => { setEditPopup(prev => ({ ...prev, new_ea_name: name })); setSearchEA(''); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', color: '#e6edf3', fontSize: '12px', borderBottom: '1px solid #21262d' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#21262d'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MQL5 URL */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: '#8b949e', fontSize: '12px', display: 'block', marginBottom: '6px' }}>MQL5 URL</label>
          <input
            type="text"
            placeholder="https://www.mql5.com/en/market/product/..."
            value={editPopup.mql5_url || ''}
            onChange={e => setEditPopup(prev => ({ ...prev, mql5_url: e.target.value }))}
            style={{ width: '100%', padding: '12px 16px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '13px', outline: 'none' }}
          />
          <p style={{ color: '#484f58', fontSize: '10px', margin: '4px 0 0' }}>Link to the EA on MQL5 marketplace</p>
        </div>

        {/* Label/Notes */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{ color: '#8b949e', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Label/Notes</label>
          <input
            type="text"
            placeholder="e.g., Set 1, Gold Settings..."
            value={editPopup.label || ''}
            onChange={e => setEditPopup(prev => ({ ...prev, label: e.target.value }))}
            style={{ width: '100%', padding: '12px 16px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '13px', outline: 'none' }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setEditPopup(null)} style={{ flex: 1, padding: '12px', background: '#21262d', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSavePopup} style={{ flex: 1, padding: '12px', background: '#a855f7', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// === RENAME GROUP POPUP ===
function RenameGroupPopup({ editGroupName, setEditGroupName, handleRenameGroup }) {
  const isNewGroup = !editGroupName.oldName;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEditGroupName(null)}>
      <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '16px', width: '400px', padding: '28px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Pencil size={16} style={{ color: '#a855f7' }} />
          <h2 style={{ color: '#e6edf3', fontSize: '16px', fontWeight: 700, margin: 0 }}>{isNewGroup ? 'Create New Group' : 'Rename Group'}</h2>
        </div>

        {!isNewGroup && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: '#8b949e', fontSize: '11px', display: 'block', marginBottom: '6px' }}>Current Name</label>
            <p style={{ color: '#484f58', fontSize: '13px', margin: 0 }}>{editGroupName.oldName}</p>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <label style={{ color: '#8b949e', fontSize: '11px', display: 'block', marginBottom: '6px' }}>{isNewGroup ? 'Group Name' : 'New Name'}</label>
          <input
            type="text"
            placeholder={isNewGroup ? 'Enter group name...' : ''}
            value={editGroupName.newName}
            onChange={e => setEditGroupName(prev => ({ ...prev, newName: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleRenameGroup()}
            style={{ width: '100%', padding: '10px 14px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '13px', outline: 'none' }}
            autoFocus
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setEditGroupName(null)} style={{ flex: 1, padding: '10px', background: '#21262d', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleRenameGroup} style={{ flex: 1, padding: '10px', background: '#a855f7', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{isNewGroup ? 'Create' : 'Rename'}</button>
        </div>
      </div>
    </div>
  );
}

// === VIEW TAB ===
function ViewTab({ icon, label, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        background: active ? `${color}15` : '#161b22',
        border: `1px solid ${active ? color : '#21262d'}`,
        borderRadius: '8px',
        color: active ? color : '#8b949e',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.2s',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}


function BulkMoveBar({ folders, selectedCount, moveTargetId, setMoveTargetId, onMove, onClear }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' }}>
      <span style={{ color: '#e6edf3', fontSize: '12px', fontWeight: 700 }}>{selectedCount} selected</span>
      <span style={{ color: '#8b949e', fontSize: '11px' }}>Move to Folder</span>
      <select value={moveTargetId} onChange={e => setMoveTargetId(e.target.value)} style={{ padding: '7px 10px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#e6edf3', fontSize: '12px', minWidth: '220px' }}>
        <option value="">Choose folder...</option>
        {folders.map(folder => <option key={folder.id} value={folder.id}>{folder.group_name}</option>)}
      </select>
      <button onClick={onMove} disabled={!moveTargetId} style={{ padding: '7px 14px', background: moveTargetId ? '#a855f7' : '#30363d', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: moveTargetId ? 'pointer' : 'not-allowed' }}>Move</button>
      <button onClick={onClear} style={{ marginLeft: 'auto', padding: '7px 10px', background: 'transparent', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', fontSize: '12px', cursor: 'pointer' }}>Clear</button>
    </div>
  );
}

function SectionLabel({ title, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '18px 0 8px' }}>
      <h2 style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 800, margin: 0 }}>{title}</h2>
      <span style={{ color: '#8b949e', fontSize: '11px' }}>{count}</span>
    </div>
  );
}

function FolderedGroupedView({ manualFolders, unfiledGrouped, expandedGroups, toggleGroup, editingMagic, editName, setEditingMagic, setEditName, handleRename, openEditPopup, setEditGroupName, handleDeleteFolder, handleRemoveMember, selectedKeys, toggleSelected, setRowsSelected }) {
  const commonRowProps = { editingMagic, editName, setEditingMagic, setEditName, handleRename, openEditPopup, selectedKeys, toggleSelected };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <SectionLabel title="My Folders" count={`${manualFolders.length} folder${manualFolders.length === 1 ? '' : 's'}`} />
      {manualFolders.map(folder => {
        const key = folderKey(folder.id);
        const isExpanded = expandedGroups[key];
        const allSelected = folder.magics.length > 0 && folder.magics.every(m => selectedKeys[getMagicIdentityKey(m)]);
        return (
          <div key={folder.id}>
            <div onClick={() => toggleGroup(key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#161b22', border: '1px solid #21262d', borderRadius: isExpanded ? '10px 10px 0 0' : '10px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {isExpanded ? <ChevronDown size={14} color="#8b949e" /> : <ChevronRight size={14} color="#8b949e" />}
                <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: folder.color || '#a855f7' }} />
                <span style={{ color: '#e6edf3', fontSize: '14px', fontWeight: 800 }}>{folder.group_name}</span>
                <span style={{ color: '#484f58', fontSize: '11px' }}>{folder.magics.length} members</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
                <FolderMetric label="Total Trades" value={(folder.total_trades || 0).toLocaleString()} />
                <FolderMetric label="Net Profit" value={money(folder.total_profit || 0)} color={(folder.total_profit || 0) >= 0 ? '#00ff88' : '#ff4757'} />
                <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                  <ActionBtn icon={<Pencil size={14} />} onClick={() => setEditGroupName({ id: folder.id, oldName: folder.group_name, newName: folder.group_name, color: folder.color || '#a855f7', description: folder.description || '' })} />
                  <ActionBtn icon={<Trash2 size={14} />} onClick={() => handleDeleteFolder(folder)} />
                </div>
              </div>
            </div>
            {isExpanded && (
              <div style={{ background: '#0d1117', border: '1px solid #21262d', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '8px 16px' }}>
                {folder.magics.length > 0 && <SelectAll rows={folder.magics} checked={allSelected} onChange={checked => setRowsSelected(folder.magics, checked)} />}
                {folder.magics.map(m => <MagicRow key={getMagicIdentityKey(m)} m={m} {...commonRowProps} selected={!!selectedKeys[getMagicIdentityKey(m)]} onSelect={() => toggleSelected(m)} onRemove={() => handleRemoveMember(folder, m)} />)}
                {folder.magics.length === 0 && <p style={{ color: '#484f58', fontSize: '12px', padding: '18px 8px', margin: 0 }}>No EAs in this folder yet.</p>}
              </div>
            )}
          </div>
        );
      })}
      {manualFolders.length === 0 && <p style={{ color: '#484f58', fontSize: '12px', padding: '14px 4px', margin: 0 }}>No folders yet.</p>}

      <SectionLabel title="Unfiled Auto-Detected EAs" count={`${unfiledGrouped.length} group${unfiledGrouped.length === 1 ? '' : 's'}`} />
      {unfiledGrouped.map(group => {
        const key = autoGroupKey(group.ea_name);
        const isExpanded = expandedGroups[key];
        const isUnmapped = group.ea_name === 'Needs Official Mapping';
        const allSelected = group.magics.length > 0 && group.magics.every(m => selectedKeys[getMagicIdentityKey(m)]);
        return (
          <div key={key}>
            <div onClick={() => toggleGroup(key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#161b22', border: '1px solid #21262d', borderRadius: isExpanded ? '10px 10px 0 0' : '10px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {isExpanded ? <ChevronDown size={14} color="#8b949e" /> : <ChevronRight size={14} color="#8b949e" />}
                <span style={{ color: isUnmapped ? '#ff4757' : '#e6edf3', fontSize: '14px', fontWeight: 800 }}>{group.ea_name}</span>
                <span style={{ color: '#484f58', fontSize: '11px' }}>({group.magics.length} auto-detected)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
                <FolderMetric label="Total Trades" value={group.total_trades.toLocaleString()} />
                <FolderMetric label="Net Profit" value={money(group.total_profit)} color={group.total_profit >= 0 ? '#00ff88' : '#ff4757'} />
              </div>
            </div>
            {isExpanded && (
              <div style={{ background: '#0d1117', border: '1px solid #21262d', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '8px 16px' }}>
                <SelectAll rows={group.magics} checked={allSelected} onChange={checked => setRowsSelected(group.magics, checked)} />
                {group.magics.map(m => <MagicRow key={getMagicIdentityKey(m)} m={m} {...commonRowProps} selected={!!selectedKeys[getMagicIdentityKey(m)]} onSelect={() => toggleSelected(m)} />)}
              </div>
            )}
          </div>
        );
      })}
      {unfiledGrouped.length === 0 && <p style={{ color: '#484f58', textAlign: 'center', padding: '30px' }}>No unfiled auto-detected EAs.</p>}
    </div>
  );
}

function SelectAll({ rows, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #21262d20', marginBottom: '6px' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ accentColor: '#a855f7' }} />
      <span style={{ color: '#8b949e', fontSize: '10px' }}>Select all ({rows.length})</span>
    </div>
  );
}

function FolderMetric({ label, value, color = '#e6edf3' }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <p style={{ color: '#8b949e', fontSize: '9px', margin: 0 }}>{label}</p>
      <p style={{ color, fontSize: '14px', fontWeight: 700, margin: 0 }}>{value}</p>
    </div>
  );
}
// === GROUPED VIEW ===
function GroupedView({ grouped, expandedGroups, toggleGroup, editingMagic, editName, setEditingMagic, setEditName, handleRename, openEditPopup, editGroupName, setEditGroupName, handleRenameGroup }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {grouped.map(group => {
        const isExpanded = expandedGroups[group.ea_name];
        const isUnmapped = group.ea_name === 'Needs Official Mapping';
        return (
          <div key={group.ea_name}>
            {/* Group Header */}
            <div
              onClick={() => toggleGroup(group.ea_name)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: '#161b22', border: '1px solid #21262d',
                borderRadius: isExpanded ? '10px 10px 0 0' : '10px', cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#30363d'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#21262d'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#8b949e', fontSize: '12px' }}>{isExpanded ? 'v' : '>'}</span>
                <span style={{ color: isUnmapped ? '#ff4757' : '#e6edf3', fontSize: '14px', fontWeight: 700 }}>
                  {group.ea_name}
                </span>
                <span style={{ color: '#8b949e', fontSize: '11px', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setEditGroupName({ oldName: group.ea_name, newName: group.ea_name }); }}>Edit</span>
                <span style={{ color: '#484f58', fontSize: '11px' }}>({group.magics.length} magic numbers)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: '#8b949e', fontSize: '9px', margin: 0 }}>Total Trades</p>
                  <p style={{ color: '#e6edf3', fontSize: '14px', fontWeight: 700, margin: 0 }}>{group.total_trades.toLocaleString()}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: '#8b949e', fontSize: '9px', margin: 0 }}>Total Profit</p>
                  <p style={{ color: group.total_profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '14px', fontWeight: 700, margin: 0 }}>
                    {group.total_profit >= 0 ? '+' : ''}${Math.abs(group.total_profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <ActionBtn icon={<Eye size={14} />} />
                  <ActionBtn icon={<Pencil size={14} />} onClick={(e) => { e.stopPropagation(); setEditGroupName({ oldName: group.ea_name, newName: group.ea_name }); }} />
                  <ActionBtn icon={<Trash2 size={14} />} />
                </div>
              </div>
            </div>

            {/* Group Members */}
            {isExpanded && (
              <div style={{ background: '#0d1117', border: '1px solid #21262d', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '8px 16px' }}>
                {/* Select All */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #21262d20', marginBottom: '6px' }}>
                  <input type="checkbox" style={{ accentColor: '#a855f7' }} />
                  <span style={{ color: '#8b949e', fontSize: '10px' }}>Select all ({group.magics.length})</span>
                </div>

                {group.magics.map(m => (
                  <MagicRow
                    key={getMagicIdentityKey(m)}
                    m={m}
                    editingMagic={editingMagic}
                    editName={editName}
                    setEditingMagic={setEditingMagic}
                    setEditName={setEditName}
                    handleRename={handleRename}
                    openEditPopup={openEditPopup}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {grouped.length === 0 && (
        <p style={{ color: '#484f58', textAlign: 'center', padding: '40px' }}>No magic numbers detected yet.</p>
      )}
    </div>
  );
}

// === LIST VIEW ===
function ListView({ sorted, expandedMagics, toggleMagic, editingMagic, editName, setEditingMagic, setEditName, handleRename, openEditPopup }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {sorted.map(m => {
        const rowKey = getMagicIdentityKey(m);
        const isExpanded = expandedMagics[rowKey];
        return (
          <div key={getMagicIdentityKey(m)}>
            {/* Row */}
            <div
              onClick={() => toggleMagic(m)}
              style={{
                display: 'flex', alignItems: 'center', padding: '10px 16px',
                background: '#161b22', border: '1px solid #21262d',
                borderRadius: isExpanded ? '10px 10px 0 0' : '10px',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#30363d'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#21262d'}
            >
              <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '10px' }}>{isExpanded ? 'v' : '>'}</span>
              <input type="checkbox" style={{ accentColor: '#a855f7', marginRight: '12px' }} onClick={e => e.stopPropagation()} />
              <span style={{ color: '#a855f7', fontSize: '13px', fontWeight: 700, fontFamily: 'monospace', marginRight: '12px', minWidth: '60px' }}>{m.magic}</span>
              <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
              <span style={{ color: '#00ff88', fontSize: '12px', fontWeight: 600, padding: '2px 8px', background: '#00ff8815', borderRadius: '4px', marginRight: '12px' }}>{m.ea_name || `EA_${m.magic}`}</span>
              <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
              <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{m.total_trades || 0} trades</span>
              <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
              <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{(m.win_rate || 0).toFixed(1)}% WR</span>
              <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
              <span style={{ color: (m.net_profit || 0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '12px', fontWeight: 700 }}>
                {(m.net_profit || 0) >= 0 ? '+' : ''}${Math.abs(m.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                <ActionBtn icon={<Pencil size={14} />} onClick={(e) => { e.stopPropagation(); openEditPopup(m); }} />
                <ActionBtn icon={<Trash2 size={14} />} />
              </div>
            </div>

            {/* Expanded Detail */}
            {isExpanded && (
              <div style={{ background: '#0d1117', border: '1px solid #21262d', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '16px 20px' }}>
                {/* Comments/Strategies tags */}
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ color: '#484f58', fontSize: '10px', marginRight: '8px' }}>Comments:</span>
                  {(m.ea_name || '').split(' ').filter(Boolean).map((tag, i) => (
                    <span key={i} style={{ padding: '2px 8px', background: '#21262d', borderRadius: '4px', color: '#8b949e', fontSize: '10px', marginRight: '4px' }}>"{tag}"</span>
                  ))}
                </div>

                {/* Detail Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                  <div>
                    <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', margin: '0 0 4px' }}>Display Name</p>
                    {editingMagic === getMagicIdentityKey(m) ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                          type="text" value={editName} onChange={e => setEditName(e.target.value)}
                          style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '4px', padding: '4px 8px', color: '#e6edf3', fontSize: '12px', width: '120px' }}
                          autoFocus onKeyDown={e => e.key === 'Enter' && handleRename(m)}
                        />
                        <button onClick={() => handleRename(m)} style={{ background: 'none', border: 'none', color: '#00ff88', cursor: 'pointer' }}><Check size={14} /></button>
                        <button onClick={() => setEditingMagic(null)} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer' }}><X size={14} /></button>
                      </div>
                    ) : (
                      <p style={{ color: '#00ff88', fontSize: '13px', fontWeight: 600, margin: 0 }}>{m.ea_name || `EA_${m.magic}`}</p>
                    )}
                  </div>
                  <div>
                    <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', margin: '0 0 4px' }}>EA</p>
                    <p style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600, margin: 0 }}>{m.ea_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', margin: '0 0 4px' }}>Trade Style</p>
                    <TradeStyleAnalysis magic={m.magic} accountId={m.account_id} />
                  </div>
                  <div>
                    <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', margin: '0 0 4px' }}>Symbols</p>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#484f58', fontSize: '9px', fontStyle: 'italic' }}>Expand in grouped view</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {sorted.length === 0 && (
        <p style={{ color: '#484f58', textAlign: 'center', padding: '40px' }}>No magic numbers found.</p>
      )}
    </div>
  );
}

// === UNMAPPED VIEW ===
function UnmappedView({ unmapped, editingMagic, editName, setEditingMagic, setEditName, handleRename, suggestions, handleAcceptSuggestion, openEditPopup, handleIdentify }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {/* Suggestions from auto-map */}
      {suggestions && suggestions.length > 0 && (
        <div style={{ marginBottom: '16px', background: '#161b22', border: '1px solid #a855f730', borderRadius: '10px', padding: '16px' }}>
          <p style={{ color: '#a855f7', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>Auto-Group Suggestions</p>
          {suggestions.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#0d1117', borderRadius: '8px', marginBottom: '6px', border: '1px solid #21262d' }}>
              <div>
                <span style={{ color: '#e6edf3', fontSize: '12px', fontWeight: 600 }}>{s.group_name}</span>
                <span style={{ color: '#8b949e', fontSize: '10px', marginLeft: '8px' }}>({s.members.length} EAs • {s.method} • {(s.confidence * 100).toFixed(0)}% confidence)</span>
              </div>
              <button onClick={() => handleAcceptSuggestion(s)} style={{ padding: '4px 12px', background: '#00ff88', border: 'none', borderRadius: '4px', color: '#0d1117', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Accept</button>
            </div>
          ))}
        </div>
      )}

      {unmapped.map(m => (
        <div key={getMagicIdentityKey(m)} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#161b22', border: '1px solid #21262d', borderRadius: '10px', transition: 'all 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#30363d'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#21262d'}
        >
          <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '10px' }}>›</span>
          <input type="checkbox" style={{ accentColor: '#a855f7', marginRight: '12px' }} />
          <span style={{ color: '#a855f7', fontSize: '13px', fontWeight: 700, fontFamily: 'monospace', marginRight: '12px', minWidth: '60px' }}>{m.magic}</span>

          <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
          <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '8px' }}>"{m.ea_name || `EA_${m.magic}`}"</span>
          <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
          <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{m.total_trades || 0} trades</span>
          <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
          <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{(m.win_rate || 0).toFixed(1)}% WR</span>
          <span style={{ fontSize: '10px', marginRight: '8px' }}>•</span>
          <span style={{ color: (m.net_profit || 0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '12px', fontWeight: 700, marginRight: '16px' }}>
            {(m.net_profit || 0) >= 0 ? '+' : ''}${Math.abs(m.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>

          {/* Suggestion indicator - only show if suggestions exist */}
          {suggestions && suggestions.length > 0 && (
            <span style={{ color: '#a855f7', fontSize: '10px', marginRight: '16px' }}>Suggestion available</span>
          )}

          {/* Action buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {editingMagic === getMagicIdentityKey(m) ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input
                  type="text" value={editName} onChange={e => setEditName(e.target.value)}
                  style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '4px', padding: '4px 8px', color: '#e6edf3', fontSize: '11px', width: '100px' }}
                  autoFocus onKeyDown={e => e.key === 'Enter' && handleRename(m)}
                />
                <button onClick={() => handleRename(m)} style={{ background: '#00ff88', border: 'none', borderRadius: '4px', padding: '4px 8px', color: '#0d1117', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                <button onClick={() => setEditingMagic(null)} style={{ background: '#21262d', border: 'none', borderRadius: '4px', padding: '4px 8px', color: '#8b949e', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setEditingMagic(getMagicIdentityKey(m)); setEditName(m.ea_name || ''); }}
                  style={{ padding: '5px 12px', background: '#00ff8815', border: '1px solid #00ff8830', borderRadius: '6px', color: '#00ff88', fontSize: '10px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  Name it
                </button>
                <button onClick={() => handleIdentify(m)} style={{ padding: '5px 12px', background: '#a855f715', border: '1px solid #a855f730', borderRadius: '6px', color: '#a855f7', fontSize: '10px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Identify
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      {unmapped.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ fontSize: '32px', marginBottom: '8px' }}>Done</p>
          <p style={{ color: '#00ff88', fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>All magic numbers are mapped!</p>
          <p style={{ color: '#8b949e', fontSize: '12px' }}>Every magic number has been assigned to an EA.</p>
        </div>
      )}
    </div>
  );
}

// === MAGIC ROW (used in grouped view) ===
function MagicRow({ m, editingMagic, editName, setEditingMagic, setEditName, handleRename, openEditPopup, selected = false, onSelect, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [symbols, setSymbols] = useState(null);
  const profitColor = (m.net_profit || 0) >= 0 ? '#00ff88' : '#ff4757';

  // Fetch symbols when expanded
  const handleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && !symbols) {
      apiGet(`/api/ea-stats/${m.magic}/detail${m.account_id ? `?account_id=${m.account_id}&ea_name=${encodeURIComponent(m.ea_name || "")}` : ""}`)
        .then(data => {
          const trades = data.recent_trades || [];
          const uniqueSymbols = [...new Set(trades.map(t => t.symbol).filter(Boolean))];
          setSymbols(uniqueSymbols);
        })
        .catch(() => setSymbols([]));
    }
  };
  return (
    <div style={{ marginBottom: '2px' }}>
      {/* Main Row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: expanded ? '6px 6px 0 0' : '6px', background: expanded ? '#161b2260' : 'transparent', transition: 'background 0.2s' }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = '#161b2280'; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Expand arrow */}
        <span onClick={handleExpand} style={{ color: '#8b949e', fontSize: '11px', marginRight: '8px', cursor: 'pointer', userSelect: 'none' }}>{expanded ? 'v' : '>'}</span>
        <input type="checkbox" checked={selected} onChange={onSelect} style={{ accentColor: '#a855f7', marginRight: '12px' }} />
        <span style={{ color: '#a855f7', fontSize: '12px', fontWeight: 700, fontFamily: 'monospace', marginRight: '10px', minWidth: '55px' }}>{m.magic}</span>
        <span style={{ fontSize: '10px', color: '#484f58', marginRight: '8px' }}>•</span>
        <span style={{ color: '#00ff88', fontSize: '11px', fontWeight: 600, padding: '2px 8px', background: '#00ff8815', borderRadius: '4px', marginRight: '12px' }}>{m.ea_name || `EA_${m.magic}`}</span>
        <span style={{ fontSize: '10px', color: '#484f58', marginRight: '8px' }}>•</span>
        <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{m.total_trades || 0} trades</span>
        <span style={{ fontSize: '10px', color: '#484f58', marginRight: '8px' }}>•</span>
        <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '12px' }}>{(m.win_rate || 0).toFixed(1)}% WR</span>
        <span style={{ fontSize: '10px', color: '#484f58', marginRight: '8px' }}>•</span>
        <span style={{ color: profitColor, fontSize: '12px', fontWeight: 700 }}>
          {(m.net_profit || 0) >= 0 ? '+' : ''}${Math.abs(m.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>

        {/* Action icons on right */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <ActionBtn icon={<Eye size={14} />} />
          <ActionBtn icon={<Pencil size={14} />} onClick={() => openEditPopup(m)} />
          {onRemove && <ActionBtn icon={<Trash2 size={14} />} onClick={onRemove} />}
        </div>
      </div>

      {/* Expanded Detail Panel */}
      {expanded && (
        <div style={{ background: '#0d111780', borderRadius: '0 0 6px 6px', padding: '12px 16px 12px 40px', borderTop: '1px solid #21262d20' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 1fr', gap: '20px' }}>
            <div>
              <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Display Name</p>
              <p style={{ color: '#00ff88', fontSize: '13px', fontWeight: 600, margin: 0 }}>{m.ea_name || `EA_${m.magic}`}</p>
            </div>
            <div>
              <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Trade Style</p>
              <TradeStyleAnalysis magic={m.magic} accountId={m.account_id} trades={symbols ? null : undefined} />
            </div>
            <div>
              <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Symbols</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {symbols === null && <span style={{ color: '#484f58', fontSize: '10px', fontStyle: 'italic' }}>Loading...</span>}
                {symbols && symbols.length === 0 && <span style={{ color: '#484f58', fontSize: '10px', fontStyle: 'italic' }}>No trades yet</span>}
                {symbols && symbols.map(sym => (
                  <span key={sym} style={{ padding: '2px 8px', background: '#378ADD15', border: '1px solid #378ADD30', borderRadius: '4px', color: '#378ADD', fontSize: '10px' }}>{sym}</span>
                ))}
              </div>
            </div>
            <div>
              <p style={{ color: '#484f58', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Status</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ padding: '2px 8px', background: m.status === 'EXCELLENT' ? '#00ff8815' : m.status === 'GOOD' ? '#378ADD15' : m.status === 'POOR' ? '#ff475715' : '#f59e0b15', borderRadius: '4px', color: m.status === 'EXCELLENT' ? '#00ff88' : m.status === 'GOOD' ? '#378ADD' : m.status === 'POOR' ? '#ff4757' : '#f59e0b', fontSize: '10px', fontWeight: 600 }}>{m.status || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === TRADE STYLE ANALYSIS COMPONENT ===
function TradeStyleAnalysis({ magic, accountId }) {
  const [styles, setStyles] = useState(null);

  useEffect(() => {
    // Fetch trade data and analyze style per symbol
    apiGet(`/api/ea-stats/${magic}/detail${accountId ? `?account_id=${accountId}` : ""}`)
      .then(data => {
        const trades = [...(data.recent_trades || []), ...(data.open_trades || [])];
        if (trades.length === 0) { setStyles([]); return; }

        // Group trades by symbol
        const bySymbol = {};
        trades.forEach(t => {
          const sym = t.symbol || 'Unknown';
          if (!bySymbol[sym]) bySymbol[sym] = [];
          bySymbol[sym].push(t);
        });

        // Analyze style per symbol
        const results = Object.entries(bySymbol).map(([symbol, symTrades]) => {
          // Calculate average duration in hours
          const durations = symTrades.filter(t => t.duration_seconds > 0).map(t => t.duration_seconds);
          const avgDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
          const avgHours = avgDuration / 3600;

          // Determine style based on average hold time
          let style = 'Unknown';
          if (avgHours < 0.25) style = 'Scalper';           // < 15 min
          else if (avgHours < 4) style = 'Day Trader';      // 15 min - 4 hours
          else if (avgHours < 24) style = 'Intraday';       // 4 - 24 hours
          else if (avgHours < 168) style = 'Swing Trader';  // 1 - 7 days
          else style = 'Position Trader';                    // > 7 days

          return { symbol, style, trades: symTrades.length, avgHours };
        });

        setStyles(results);
      })
      .catch(() => setStyles([]));
  }, [magic, accountId]);

  if (styles === null) return <span style={{ color: '#484f58', fontSize: '10px', fontStyle: 'italic' }}>Analyzing...</span>;
  if (styles.length === 0) return <span style={{ color: '#484f58', fontSize: '10px', fontStyle: 'italic' }}>No trades</span>;

  // If all symbols have same style, show once
  const uniqueStyles = [...new Set(styles.map(s => s.style))];

  if (uniqueStyles.length === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {styles.map(s => (
          <div key={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#e6edf3', fontSize: '11px', fontWeight: 600 }}>{s.style}</span>
            <span style={{ padding: '1px 6px', background: '#f59e0b15', border: '1px solid #f59e0b30', borderRadius: '3px', color: '#f59e0b', fontSize: '9px' }}>({s.symbol})</span>
          </div>
        ))}
      </div>
    );
  }

  // Multiple styles per symbol
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {styles.map(s => (
        <div key={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: '#e6edf3', fontSize: '11px', fontWeight: 600 }}>{s.style}</span>
          <span style={{ padding: '1px 6px', background: '#f59e0b15', border: '1px solid #f59e0b30', borderRadius: '3px', color: '#f59e0b', fontSize: '9px' }}>({s.symbol})</span>
        </div>
      ))}
    </div>
  );
}

// === ACTION BUTTON ===
function ActionBtn({ icon, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#484f58', fontSize: '12px', padding: '2px 4px', borderRadius: '4px', transition: 'color 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.color = '#e6edf3'}
      onMouseLeave={e => e.currentTarget.style.color = '#484f58'}
    >
      {icon}
    </button>
  );
}









